import { CancelledError, deleteFile, mountInputFile, readFile, runFFmpeg } from './ffmpeg';
import { inspectFile, type MediaInfo } from './inspect';
import { outputExtension, outputMimeType, planConversion, type ConvertPlan, type OutputFormat, type PlanOptions } from './plan';
import { probeWebCodecs, transcodeToMp4WithWebCodecs, WebCodecsUnsupportedError } from './webcodecs';

export interface ConvertResult {
  blob: Blob;
  outputName: string;
  format: OutputFormat;
  method: ConvertPlan['method'];
  durationMs: number;
  info: MediaInfo;
  plan: ConvertPlan;
  /** Non-fatal issues surfaced during the run (e.g. A/V drift). Empty on
   *  the ffmpeg path today; populated by the WebCodecs path when its post-
   *  encode sanity checks find something worth flagging to the UI. */
  warnings: string[];
}

export interface ConvertOptions {
  format?: OutputFormat;
  onProgress?: (fraction: number) => void;
  outputBasename?: string;
  signal?: AbortSignal;
  /**
   * Skip the internal inspect step by passing pre-computed metadata. Wave's
   * UI inspects on file drop to build the plan callout, so re-inspecting here
   * would cost another mount + full ffmpeg run.
   */
  precomputedInfo?: MediaInfo;
  /**
   * Optional File System Access writable stream. When present and the
   * chosen path is WebCodecs, the muxer streams to disk instead of
   * buffering the whole MP4 in RAM — the returned `blob` will be empty and
   * the real output is on disk. Ignored on the ffmpeg mpeg4 fallback (which
   * writes a MEMFS file we then read into a blob).
   */
  streamTarget?: FileSystemWritableFileStream;
  /** Compatibility, target size, trim, rotate, resize and audio options. */
  planOptions?: PlanOptions;
}

const extensionOf = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : 'bin';
};

const stripExtension = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(0, idx) : name;
};

export async function convert(file: File, opts: ConvertOptions = {}): Promise<ConvertResult> {
  const start = performance.now();
  const format: OutputFormat = opts.format ?? 'mp4';

  // Kick off metadata inspect and WebCodecs probe in parallel; both are cheap
  // and their outputs together determine which backend the plan picks.
  const [info, support] = await Promise.all([
    opts.precomputedInfo ? Promise.resolve(opts.precomputedInfo) : inspectFile(file),
    probeWebCodecs().catch(() => null),
  ]);
  if (opts.signal?.aborted) throw new CancelledError();

  // First-pass plan uses a placeholder input name — good enough for a
  // WebCodecs plan (which ignores args) and for deciding the encoder. If we
  // end up on the ffmpeg path, we re-plan with the real mounted path so the
  // ffmpeg args carry the WORKERFS mount point.
  const initialPlan = planConversion(info, 'input-placeholder', format, support, opts.planOptions);
  if (initialPlan.blocker) throw new Error(initialPlan.blocker);

  if (initialPlan.encoder === 'webcodecs') {
    try {
      const wc = await transcodeToMp4WithWebCodecs({
        file,
        info,
        support: support!,
        decodeStrategy: initialPlan.decodeStrategy ?? 'browser',
        videoCodec: initialPlan.videoCodec ?? 'h264',
        encode: initialPlan.encode,
        audio: initialPlan.audio,
        trim: initialPlan.trim,
        streamTarget: opts.streamTarget,
        onProgress: opts.onProgress,
        signal: opts.signal,
      });
      if (opts.signal?.aborted) throw new CancelledError();
      return {
        blob: wc.blob,
        outputName: `${opts.outputBasename ?? stripExtension(file.name)}.${outputExtension(format)}`,
        format,
        method: initialPlan.method,
        durationMs: performance.now() - start,
        info,
        // Report the decode path that actually ran: a hardware-decode plan
        // can fall back to ffmpeg decode partway.
        plan: { ...initialPlan, decodeStrategy: wc.decodeStrategy },
        warnings: [...wc.warnings, ...targetWarnings(initialPlan, wc.blob.size)],
      };
    } catch (err) {
      if (opts.signal?.aborted) throw new CancelledError();
      if (!(err instanceof WebCodecsUnsupportedError)) throw err;
      // Fall through to ffmpeg by re-planning without WebCodecs support.
      // This is the whole point of the WebCodecsUnsupportedError sentinel:
      // the browser said it could decode the source but it turned out it
      // couldn't. The wasm mpeg4 encoder is our safety net.
    }
  }

  return runFfmpegPlan(file, info, format, opts, start);
}

async function runFfmpegPlan(
  file: File,
  info: MediaInfo,
  format: OutputFormat,
  opts: ConvertOptions,
  start: number
): Promise<ConvertResult> {
  const ffmpegInputName = `input.${extensionOf(file.name)}`;

  // Mount the source via WORKERFS so ffmpeg reads directly from the browser's
  // Blob storage — no full copy into the wasm heap. `plan.args` receives the
  // mounted path as its input reference so it works regardless of mount vs
  // MEMFS fallback. Force support=null here so the planner picks an ffmpeg
  // plan even when the browser also had WebCodecs (used on the fall-through
  // path when WebCodecs refused a source it looked like it could handle).
  const mount = await mountInputFile(file, ffmpegInputName);
  const plan = planConversion(info, mount.path, format, null, opts.planOptions);

  try {
    let { code, logs } = await runFFmpeg(plan.args, {
      onProgress: opts.onProgress,
      durationSeconds: info.durationSeconds,
      signal: opts.signal,
    });
    if (opts.signal?.aborted) throw new CancelledError();

    // If the first attempt failed with a demuxer signal that longer probing
    // would have caught (short probesize, malformed leading data, missing
    // stream info), retry once with a much more generous probe budget. Wave
    // never surfaces this to the user — it feels like the file "just worked".
    if (code !== 0 && needsPermissiveRetry(logs)) {
      const retryArgs = withPermissiveProbe(plan.args);
      const retry = await runFFmpeg(retryArgs, {
        onProgress: opts.onProgress,
        durationSeconds: info.durationSeconds,
        signal: opts.signal,
      });
      if (opts.signal?.aborted) throw new CancelledError();
      code = retry.code;
      logs = retry.logs;
    }

    if (code !== 0) {
      const hint = logs.slice(-40).join('\n');
      throw new Error(`ffmpeg exited with code ${code}. ${extractFailureReason(hint)}`);
    }

    const output = await readFile(plan.outputName);
    // Copy into a plain ArrayBuffer to detach from the ffmpeg heap.
    const detached = new Uint8Array(output.byteLength);
    detached.set(output);
    const blob = new Blob([detached], { type: outputMimeType(format) });

    const outputName = `${opts.outputBasename ?? stripExtension(file.name)}.${outputExtension(format)}`;

    return {
      blob,
      outputName,
      format,
      method: plan.method,
      durationMs: performance.now() - start,
      info,
      plan,
      warnings: targetWarnings(plan, blob.size),
    };
  } finally {
    await mount.unmount();
    await deleteFile(plan.outputName);
  }
}

/** Encoders land near, not exactly on, a requested bitrate. Flag a file that
 *  came out over its target so the user knows before they try to send it.
 *  A streamed-to-disk output has an empty blob and is skipped. */
function targetWarnings(plan: ConvertPlan, outputBytes: number): string[] {
  if (!plan.targetBytes || !outputBytes || outputBytes <= plan.targetBytes) return [];
  const mb = (b: number) => (b / 1_000_000).toFixed(1);
  return [`The file came out at ${mb(outputBytes)} MB, a little over the ${mb(plan.targetBytes)} MB target. Choose a slightly smaller size and convert again.`];
}

/** Back-compat: keep the MP4-specific entrypoint used by existing callers. */
export const convertToMp4 = (file: File, opts: ConvertOptions = {}) =>
  convert(file, { ...opts, format: 'mp4' });

/** Signals in the log that suggest the source needed a longer probe.
 *  Retrying with a larger analyzeduration/probesize very often unblocks. */
function needsPermissiveRetry(logs: string[]): boolean {
  const tail = logs.slice(-60).join('\n');
  return /could not find codec parameters|failed to open|invalid data found when processing input|probesize|analyzeduration/i.test(tail);
}

/** Insert `-analyzeduration` / `-probesize` right before the first `-i`. */
function withPermissiveProbe(args: string[]): string[] {
  const inputIdx = args.indexOf('-i');
  if (inputIdx < 0) return args;
  const out = args.slice();
  out.splice(inputIdx, 0, '-analyzeduration', '100M', '-probesize', '100M');
  return out;
}

/** Extract a one-line reason from ffmpeg's tail log for surfacing to the user. */
function extractFailureReason(tail: string): string {
  const lines = tail.split('\n').filter((l) => l.trim().length > 0);
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const l = lines[i];
    if (/^\s*(Error|Invalid|Failed|Cannot|Could not|Unsupported)\b/i.test(l)) {
      return l.trim();
    }
  }
  return lines[lines.length - 1]?.trim() ?? '';
}
